import {defineMessages, FormattedMessage} from 'react-intl';
import {useCallback, useEffect, useState} from 'react';

import {Button, Icon, Spinner} from '@sail/ui';
import {cancelCircle, customers} from '@sail/icons/react/Icon';

import {useConversationUIFeatureFlags} from '@stripe-internal/conversations-ui';

import {useApp} from '../../../core/hooks/appHooks';

const POLL_INTERVAL_MS = 3000;
const buttonCss = {flex: 1, gap: 'small', height: '28px'};

const messages = defineMessages({
  shareScreenButton: {
    id: 'conversations_assistant.screenshareButton.shareScreenButton',
    description: 'Button to share screen',
    defaultMessage: 'Share screen',
  },
  waitingForAgent: {
    id: 'conversations_assistant.screenshareButton.waitingForAgent',
    description: 'Button text when waiting for agent',
    defaultMessage: 'Waiting for agent',
  },
  stopSharing: {
    id: 'conversations_assistant.screenshareButton.stopSharing',
    description: 'Button text when stopping screenshare',
    defaultMessage: 'Stop sharing',
  },
});

type ScreenshareStatus = 'ACTIVE' | 'INACTIVE' | 'WAITING_FOR_AGENT';
type ScreenshareChannel = 'chat' | 'rac';
type ScreenshareActionName =
  | 'get-screenshare-status'
  | 'is_screenshare_active'
  | 'set-screenshare-name'
  | 'request-screenshare'
  | 'stop-screenshare';

type Props = {
  channel: ScreenshareChannel;
  agentNames?: string[] | null;
};

function getScreenshareStatus(data: unknown): ScreenshareStatus {
  if (typeof data !== 'object' || data === null || !('state' in data)) {
    return 'INACTIVE';
  }

  const {state} = data as {state?: unknown};
  return state === 'ACTIVE' ||
    state === 'INACTIVE' ||
    state === 'WAITING_FOR_AGENT'
    ? state
    : 'INACTIVE';
}

function getLegacyScreenshareActive(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    'is_screenshare_active' in data &&
    data.is_screenshare_active === true
  );
}

export function ConversationScreenshareButton({channel, agentNames}: Props) {
  const app = useApp();
  const {enableScreenshareContextProvider} = useConversationUIFeatureFlags();
  const [screenshareStatus, setScreenshareStatus] =
    useState<ScreenshareStatus>('INACTIVE');
  const [isLegacyScreenshareActive, setIsLegacyScreenshareActive] =
    useState(false);

  const firstAgentName = agentNames?.find((name) => name.length > 0);

  const requestAction = useCallback(
    (
      name: ScreenshareActionName,
      data: Record<string, unknown> = {},
      waitForResponse = false,
    ) => {
      return app.requestAction({
        action: {
          name,
          data,
        },
        waitForResponse,
      });
    },
    [app],
  );

  const refreshStatus = useCallback(async () => {
    try {
      if (enableScreenshareContextProvider) {
        const response = await requestAction(
          'get-screenshare-status',
          {},
          true,
        );
        setScreenshareStatus(getScreenshareStatus(response.data));
        return;
      }

      const response = await requestAction('is_screenshare_active', {}, true);
      setIsLegacyScreenshareActive(getLegacyScreenshareActive(response.data));
    } catch (error) {
      if (enableScreenshareContextProvider) {
        setScreenshareStatus('INACTIVE');
      } else {
        setIsLegacyScreenshareActive(false);
      }
    }
  }, [enableScreenshareContextProvider, requestAction]);

  useEffect(() => {
    refreshStatus();

    const intervalId = window.setInterval(() => {
      refreshStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshStatus]);

  useEffect(() => {
    if (!enableScreenshareContextProvider || !firstAgentName) {
      return;
    }

    requestAction(
      'set-screenshare-name',
      {
        agentName: firstAgentName,
        channel,
      },
      true,
    );
  }, [
    channel,
    enableScreenshareContextProvider,
    firstAgentName,
    requestAction,
  ]);

  const requestScreenshare = useCallback(() => {
    requestAction('request-screenshare');
  }, [requestAction]);

  const stopScreenshare = useCallback(() => {
    requestAction('stop-screenshare');
  }, [requestAction]);

  if (
    enableScreenshareContextProvider &&
    screenshareStatus === 'WAITING_FOR_AGENT'
  ) {
    return (
      <Button
        size="small"
        onPress={stopScreenshare}
        data-testid="conversations-widget-share-screen-button"
        css={buttonCss}
      >
        <Spinner />
        <FormattedMessage {...messages.waitingForAgent} />
      </Button>
    );
  }

  if (enableScreenshareContextProvider && screenshareStatus === 'ACTIVE') {
    return (
      <Button
        size="small"
        onPress={stopScreenshare}
        type="destructive"
        data-testid="conversations-widget-share-screen-button"
        css={buttonCss}
      >
        <Icon icon={cancelCircle} />
        <FormattedMessage {...messages.stopSharing} />
      </Button>
    );
  }

  const isDisabled = enableScreenshareContextProvider
    ? !firstAgentName
    : !firstAgentName || isLegacyScreenshareActive;

  return (
    <Button
      size="small"
      onPress={requestScreenshare}
      data-testid="conversations-widget-share-screen-button"
      css={buttonCss}
      disabled={isDisabled}
    >
      <Icon icon={customers} />
      <FormattedMessage {...messages.shareScreenButton} />
    </Button>
  );
}
